import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Table,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  Badge,
  Container,
  Button,
  Modal,
} from "react-bootstrap";
import api, { BASE_URL } from "../../services/api";
import { generateSertifikat } from "../../utils/generateSertifikat";
import "./Dashboard.css";

const ROMAN_MONTHS = ["I","II","III","IV","V","VI","VII","VIII","IX","X","XI","XII"];

const jenisPelatihanLabel = {
  PPH01: "Penyelia Halal UMK Self Declare",
  PPH02: "Penyelia Halal UMK Reguler",
  PPH03: "Penyelia Halal Reguler",
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const PesertaPenerbitanSertifikat = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [jenisList, setJenisList] = useState([]);
  const [angkatanList, setAngkatanList] = useState([]);
  const [filterJenis, setFilterJenis] = useState("");
  const [filterAngkatan, setFilterAngkatan] = useState("");
  const [searchText, setSearchText] = useState("");

  // Per-row generating state: { [email]: true }
  const [generating, setGenerating] = useState({});

  // Modal state
  const [showCertModal, setShowCertModal] = useState(false);
  const [certTarget, setCertTarget]       = useState(null);   // full data from API
  const [certTargetRow, setCertTargetRow] = useState(null);   // row from table
  const [nomorSertifikat, setNomorSertifikat] = useState("");
  const [tglTerbit, setTglTerbit]         = useState("");
  const [certModalError, setCertModalError] = useState("");
  const [certSaving, setCertSaving]       = useState(false);
  const nomorInputRef = useRef(null);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filterJenis, filterAngkatan]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchFilterOptions = async () => {
    try {
      const res = await api.get("/pendaftaran-penyelia/filter-options");
      if (res.data.success) {
        setJenisList(res.data.jenis_pelatihan || []);
        setAngkatanList(res.data.angkatan || []);
      }
    } catch (err) {
      console.error("Error fetching filter options:", err);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = {};
      if (filterJenis) params.jenis_pelatihan = filterJenis;
      if (filterAngkatan) params.angkatan = filterAngkatan;
      const res = await api.get("/pendaftaran-penyelia/daftar-lulus", { params });
      if (res.data.success) {
        setData(res.data.data);
      } else {
        setError("Gagal memuat data.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Gagal memuat data dari server.");
    } finally {
      setLoading(false);
    }
  }, [filterJenis, filterAngkatan]);

  const filteredData = data.filter((row) => {
    if (!searchText) return true;
    const term = searchText.toLowerCase();
    return (
      row.nama?.toLowerCase().includes(term) ||
      row.email?.toLowerCase().includes(term) ||
      row.no_hp?.toLowerCase().includes(term) ||
      row.nik?.toLowerCase().includes(term) ||
      row.no_register?.toLowerCase().includes(term)
    );
  });

  // Step 1: fetch data & open modal
  const handleOpenCertModal = async (row) => {
    try {
      setGenerating((prev) => ({ ...prev, [row.email]: true }));
      const res = await api.get(
        `/pendaftaran-penyelia/sertifikat-data/${encodeURIComponent(row.email)}`
      );
      if (!res.data.success) throw new Error(res.data.message || "Data tidak ditemukan");

      const data = res.data.data;
      const now   = new Date();
      const defNo = `.../PH.1.B/HC-UINSK/${ROMAN_MONTHS[now.getMonth()]}/${now.getFullYear()}`;
      const defTgl = now.toISOString().slice(0, 10); // YYYY-MM-DD

      setCertTarget(data);
      setCertTargetRow(row);
      // Jika sudah pernah digenerate, prefill dengan nomor & tanggal sebelumnya
      setNomorSertifikat(row.no_sertifikat || defNo);
      setTglTerbit(row.tgl_terbit ? row.tgl_terbit.slice(0, 10) : defTgl);
      setCertModalError("");
      setShowCertModal(true);
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Gagal memuat data");
    } finally {
      setGenerating((prev) => ({ ...prev, [row.email]: false }));
    }
  };

  // Step 2: generate PDF, upload, download
  const handleConfirmGenerate = async () => {
    if (!nomorSertifikat.trim()) { setCertModalError("Nomor sertifikat wajib diisi"); return; }
    if (nomorSertifikat.trim().startsWith("...")) { setCertModalError("Nomor urut sertifikat belum diisi. Ganti '...' dengan nomor urut yang benar."); return; }
    if (!tglTerbit)              { setCertModalError("Tanggal terbit wajib diisi"); return; }

    try {
      setCertSaving(true);
      setCertModalError("");

      const { blob, filename } = await generateSertifikat(certTarget, BASE_URL, nomorSertifikat.trim(), tglTerbit);

      // Upload PDF + simpan data ke backend
      const formData = new FormData();
      formData.append("email",           certTarget.email);
      formData.append("no_sertifikat",   nomorSertifikat.trim());
      formData.append("tgl_terbit",      tglTerbit);
      formData.append("file_sertifikat", blob, filename);

      await api.post("/pendaftaran-penyelia/sertifikat", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setShowCertModal(false);
      fetchData(); // refresh grid agar link download muncul
    } catch (err) {
      setCertModalError(err.response?.data?.message || err.message || "Gagal menyimpan sertifikat");
    } finally {
      setCertSaving(false);
    }
  };

  return (
    <>
    <Container fluid>
      <div className="dashboard-header mb-4">
        <h2>
          <i className="fas fa-certificate me-2"></i>
          Penerbitan Sertifikat
        </h2>
        <p className="text-muted mb-0">
          Peserta yang dinyatakan{" "}
          <Badge bg="success">LULUS</Badge>
        </p>
      </div>

      {/* Filter */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label className="fw-semibold mb-1">Jenis Pelatihan</Form.Label>
              <Form.Select
                value={filterJenis}
                onChange={(e) => { setFilterJenis(e.target.value); setFilterAngkatan(""); }}
              >
                <option value="">— Semua —</option>
                {jenisList.map((j) => (
                  <option key={j} value={j}>{jenisPelatihanLabel[j] || j}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="fw-semibold mb-1">Angkatan</Form.Label>
              <Form.Select
                value={filterAngkatan}
                onChange={(e) => setFilterAngkatan(e.target.value)}
              >
                <option value="">— Semua —</option>
                {angkatanList.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="fw-semibold mb-1">Cari</Form.Label>
              <Form.Control
                type="text"
                placeholder="Nama, email, NIK, no. register..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Col>
            <Col md={2} className="text-end">
              <span className="text-muted small">{filteredData.length} data</span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {error && <Alert variant="danger" className="m-3">{error}</Alert>}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Memuat data...</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-3">#</th>
                  <th>No. Register</th>
                  <th>Nama</th>
                  <th>Email</th>
                  <th>No. HP</th>
                  <th>NIK</th>
                  <th>Jenis Pelatihan</th>
                  <th>Angkatan</th>
                  <th>Tgl. Daftar</th>
                  <th className="text-center">Sertifikat</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center text-muted py-5">
                      <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
                      Tidak ada data
                    </td>
                  </tr>
                ) : (
                  filteredData.map((row, idx) => (
                    <tr key={row.email}>
                      <td className="ps-3">{idx + 1}</td>
                      <td>{row.no_register || "-"}</td>
                      <td className="fw-semibold">{row.nama || "-"}</td>
                      <td>{row.email}</td>
                      <td>{row.no_hp || "-"}</td>
                      <td>{row.nik || "-"}</td>
                      <td>{jenisPelatihanLabel[row.jenis_pelatihan] || row.jenis_pelatihan || "-"}</td>
                      <td>{row.angkatan || "-"}</td>
                      <td>{formatDate(row.tgl_daftar)}</td>
                      <td className="text-center">
                        <div className="d-flex flex-column align-items-center gap-1">
                          <Button
                            size="sm"
                            variant={row.file_sertifikat ? "outline-primary" : "primary"}
                            onClick={() => handleOpenCertModal(row)}
                            disabled={!!generating[row.email]}
                          >
                            {generating[row.email]
                              ? <><Spinner animation="border" size="sm" className="me-1" />Memuat...</>
                              : <><i className="fas fa-file-pdf me-1"></i>{row.file_sertifikat ? "Generate Ulang" : "Generate Sertifikat"}</>}
                          </Button>
                          {row.file_sertifikat && (
                            <a
                              href={`${BASE_URL}/uploads/sertifikat/${row.file_sertifikat}`}
                              target="_blank"
                              rel="noreferrer"
                              className="small"
                            >
                              <i className="fas fa-download me-1"></i>Unduh
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>

      {/* Modal input nomor sertifikat & tanggal terbit */}
      <Modal show={showCertModal} onHide={() => !certSaving && setShowCertModal(false)} centered
        onEntered={() => {
          const el = nomorInputRef.current;
          if (el) {
            el.focus();
            const dotStart = el.value.indexOf("...");
            if (dotStart !== -1) el.setSelectionRange(dotStart, dotStart + 3);
          }
        }}
      >
        <Modal.Header closeButton={!certSaving}>
          <Modal.Title>
            <i className="fas fa-certificate me-2"></i>Terbitkan Sertifikat
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {certTargetRow && (
            <p className="text-muted small mb-3">
              <strong>{certTargetRow.nama}</strong> — {certTargetRow.no_register}
            </p>
          )}
          {certModalError && <Alert variant="danger" className="py-2">{certModalError}</Alert>}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Nomor Sertifikat <span className="text-danger">*</span></Form.Label>
            <Form.Control
              ref={nomorInputRef}
              type="text"
              value={nomorSertifikat}
              onChange={(e) => setNomorSertifikat(e.target.value)}
              placeholder="Contoh: 0001/PH.1.B/HC-UINSK/V/2026"
              disabled={certSaving}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label className="fw-semibold">Tanggal Terbit <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="date"
              value={tglTerbit}
              onChange={(e) => setTglTerbit(e.target.value)}
              disabled={certSaving}
            />
            <Form.Text className="text-muted">Tanggal ini akan muncul di atas tanda tangan pada sertifikat.</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCertModal(false)} disabled={certSaving}>
            Batal
          </Button>
          <Button variant="primary" onClick={handleConfirmGenerate} disabled={certSaving}>
            {certSaving
              ? <><Spinner animation="border" size="sm" className="me-1" />Memproses...</>
              : <><i className="fas fa-save me-1"></i>Generate & Simpan</>}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PesertaPenerbitanSertifikat;
